import { useCallback, useMemo } from 'react';

import { useAnalyticsContext } from './context';

type TrackFn = (eventName: string, data: Record<string, unknown>) => void;

type DebtsTracker = {
  created: (params: {
    debtType?: string | null;
    companyId?: string | null;
    institution?: string | null;
    borrowedAmount?: number | null;
    amortizationType?: string | null;
  }) => void;

  edited: (params: {
    debtId: string;
    debtType?: string | null;
  }) => void;

  deleted: (params: {
    debtId: string;
    debtType?: string | null;
  }) => void;

  detailViewed: (params: {
    debtId: string;
    companyId?: string | null;
    institution?: string | null;
  }) => void;

  paymentMade: (params: {
    debtId: string;
    paymentAmount?: number | null;
  }) => void;

  sorted: (params: {
    sortField: string;
    sortDirection: string;
  }) => void;
};

export const useDebtsTracking = (): DebtsTracker => {
  const analytics = useAnalyticsContext();

  const track: TrackFn = useCallback((eventName, data) => {
    try {
      analytics?.mixPanel?.trackContinueEvent?.({
        customEvent: eventName,
        module: 'debts',
        ...data,
      });
    } catch {
      // Tracking must never break the UI.
    }
  }, [analytics]);

  return useMemo<DebtsTracker>(() => ({
    created: (params) => track('debt_created', params),
    edited: (params) => track('debt_edited', params),
    deleted: (params) => track('debt_deleted', params),
    detailViewed: (params) => track('debt_detail_viewed', params),
    paymentMade: (params) => track('debt_payment_made', params),
    sorted: (params) => track('debts_sorted', params),
  }), [track]);
};
